import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, Loader2, Download, Truck } from "lucide-react";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Bouton admin pour générer une étiquette Sendcloud à partir d'une commande.
 * - Charge dynamiquement la liste des méthodes d'expédition Sendcloud (Mondial Relay 24R, Colissimo, etc.)
 * - Crée le colis + récupère l'étiquette PDF + numéro de suivi en un appel
 *
 * Props:
 *  - order (object)            : la commande
 *  - onLabelGenerated(data)    : callback avec { parcel_id, tracking_number, tracking_url, label_url }
 */
export default function SendcloudLabelGenerator({ order, onLabelGenerated }) {
  const [methods, setMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState("");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [fetchError, setFetchError] = useState("");

  // Récupère les méthodes d'expédition disponibles
  useEffect(() => {
    let cancelled = false;
    const fetchMethods = async () => {
      setLoading(true);
      setFetchError("");
      try {
        // NB : on ne filtre PAS par service_point_id côté listing — sinon Sendcloud
        // renvoie 0 méthodes si le point relais n'est pas reconnu. On laisse l'admin
        // choisir la méthode et c'est seulement au moment de créer le colis que
        // service_point_id sera transmis (cf. endpoint POST /orders/{id}/sendcloud/label).
        const params = { to_country: order.shipping_address?.country || "FR" };
        const res = await axios.get(`${API}/sendcloud/shipping-methods`, { params, withCredentials: true });
        if (cancelled) return;
        const list = res.data?.shipping_methods || [];
        setMethods(list);
        // Pré-sélection intelligente
        if (list.length > 0) {
          const preferred = list.find(m => {
            const n = (m.name || "").toLowerCase();
            if (order.delivery_method === "mondial_relay_point") return n.includes("mondial relay") && (n.includes("24r") || n.includes("point") || n.includes("relais"));
            if (order.delivery_method === "mondial_relay_home") return n.includes("mondial relay") && (n.includes("home") || n.includes("dom"));
            if (order.delivery_method === "colissimo") return n.includes("colissimo") && (n.includes("home") || n.includes("dom"));
            return n.includes("colissimo");
          });
          setSelectedMethod(String(preferred?.id || list[0].id));
        }
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Erreur inconnue";
        setFetchError(detail);
        toast.error("Impossible de charger les méthodes d'expédition Sendcloud");
      } finally {
        setLoading(false);
      }
    };
    fetchMethods();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.order_id]);

  const handleGenerate = async () => {
    if (!selectedMethod) {
      toast.error("Choisis une méthode d'expédition");
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/orders/${order.order_id}/sendcloud/label`,
        { shipping_method_id: Number(selectedMethod) },
        { withCredentials: true }
      );
      toast.success(`Étiquette générée : #${res.data.tracking_number || res.data.parcel_id}`);
      if (onLabelGenerated) onLabelGenerated(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Échec de la génération");
    } finally {
      setGenerating(false);
    }
  };

  const labelAlreadyGenerated = !!order.sendcloud_parcel_id || !!order.sendcloud_label_url;

  return (
    <div className="p-3 rounded-lg border border-[#D926A9]/30 bg-[#D926A9]/10 space-y-2.5" data-testid="sendcloud-label-generator">
      <div className="flex items-center gap-2">
        <Truck className="w-4 h-4 text-[#D926A9]" />
        <p className="text-xs text-[#F7F3FC] font-medium">Générer l'étiquette via Sendcloud</p>
      </div>
      <p className="text-[10px] text-[#B59ED1] leading-relaxed">
        Crée l'expédition + récupère l'étiquette PDF et le numéro de suivi automatiquement. Sendcloud gère Mondial Relay, Colissimo, Chronopost, etc.
      </p>

      {loading ? (
        <div className="text-center py-2 text-[#B59ED1] text-xs">
          <Loader2 className="w-4 h-4 mx-auto animate-spin" />
          Chargement des méthodes…
        </div>
      ) : methods.length === 0 ? (
        <div className="text-xs text-amber-400 space-y-1.5">
          <p className="font-medium">Aucune méthode d'expédition disponible</p>
          {fetchError ? (
            <p className="text-[10px] text-amber-300/80 font-mono break-all">{fetchError}</p>
          ) : (
            <p className="text-[10px] text-[#B59ED1]">
              Vérifie que tes transporteurs (Mondial Relay, Colissimo…) sont activés dans{" "}
              <a href="https://panel.sendcloud.sc/v2/settings/carriers" target="_blank" rel="noreferrer" className="text-[#00E5FF] underline">
                Sendcloud Panel → Carriers
              </a>.
            </p>
          )}
        </div>
      ) : (
        <>
          <Select value={selectedMethod} onValueChange={setSelectedMethod}>
            <SelectTrigger className="bg-[#1B132B] border-white/10 text-xs h-9" data-testid="sendcloud-method-select">
              <SelectValue placeholder="Choisis une méthode d'expédition" />
            </SelectTrigger>
            <SelectContent className="bg-[#1B132B] border-white/10 text-white max-h-[300px]">
              {methods.map(m => (
                <SelectItem key={m.id} value={String(m.id)}>
                  {m.name} {m.price && `(${m.price.toFixed?.(2) || m.price} €)`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={handleGenerate}
            disabled={generating || !selectedMethod}
            className="gradient-primary w-full"
            data-testid="sendcloud-generate-btn"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération…</>
            ) : (
              <><Package className="w-4 h-4 mr-2" /> Générer l'étiquette</>
            )}
          </Button>
        </>
      )}

      {labelAlreadyGenerated && (
        <a
          href={`${API}/orders/${order.order_id}/sendcloud/label-pdf`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 mt-2 px-3 py-2 rounded-lg bg-[#1B132B] text-xs text-[#00E5FF] hover:bg-[#00E5FF]/10 border border-[#00E5FF]/30 transition-colors"
          data-testid="sendcloud-download-pdf"
        >
          <Download className="w-3 h-3" /> Télécharger l'étiquette PDF
        </a>
      )}
    </div>
  );
}
